import React, { useState, useEffect, useCallback } from 'react';
import { FaSync, FaCalculator } from 'react-icons/fa';
import { getCaptcha } from '../api/client';

const SimpleCaptcha = ({ onVerify, disabled = false }) => {
  const [captchaData, setCaptchaData] = useState(null);
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onVerifyRef = React.useRef(onVerify);
  onVerifyRef.current = onVerify;

  const fetchCaptcha = useCallback(async () => {
    setLoading(true);
    setError('');
    setAnswer('');
    try {
      const data = await getCaptcha();
      setCaptchaData(data);
      // Reset the answer in parent using ref to avoid dependency
      onVerifyRef.current('', '');
    } catch (err) {
      setError('Failed to load CAPTCHA. Please try again.');
      console.error('Failed to fetch CAPTCHA:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCaptcha();
    // Only fetch on mount, not when fetchCaptcha changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAnswerChange = (e) => {
    const value = e.target.value;
    setAnswer(value);
    // Pass the token and answer to parent component
    if (captchaData?.token) {
      onVerify(captchaData.token, value);
    }
  };

  const handleRefresh = (e) => {
    e.preventDefault();
    fetchCaptcha();
  };

  if (loading && !captchaData) {
    return (
      <div className="flex items-center justify-center p-4 bg-neutral-50 rounded-lg border-2 border-neutral-300">
        <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mr-2"></div>
        <span className="text-neutral-600 text-sm">Loading CAPTCHA...</span>
      </div>
    );
  }

  if (error && !captchaData) {
    return (
      <div className="p-4 bg-red-50 rounded-lg border-2 border-red-200">
        <p className="text-red-600 text-sm mb-2">{error}</p>
        <button
          onClick={handleRefresh}
          className="text-primary-600 text-sm font-medium hover:text-primary-700 flex items-center gap-1"
        >
          <FaSync className="text-xs" />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gradient-to-br from-primary-50 to-secondary-50 rounded-lg border-2 border-neutral-300">
      <div className="flex items-center gap-2 mb-3">
        <FaCalculator className="text-primary-500" />
        <span className="text-sm font-semibold text-neutral-700">Security Check</span>
      </div>

      <div className="flex items-center gap-3">
        {/* Question Display */}
        <div className="flex-1 bg-white rounded-lg px-4 py-3 border border-neutral-300 shadow-sm">
          <p className="text-neutral-800 font-medium text-lg select-none">
            {captchaData?.question || 'Loading...'}
          </p>
        </div>

        {/* Refresh Button */}
        <button
          type="button"
          onClick={handleRefresh}
          disabled={disabled || loading}
          className="p-3 bg-white rounded-lg border border-neutral-300 text-neutral-500 hover:text-primary-600 hover:border-primary-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          title="Get new question"
        >
          <FaSync className={`text-lg ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Answer Input */}
      <div className="mt-3">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={answer}
          onChange={handleAnswerChange}
          placeholder="Enter your answer"
          disabled={disabled || loading}
          className="w-full px-4 py-3 rounded-lg border-2 border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed text-center text-lg font-medium"
          autoComplete="off"
        />
      </div>

      {error && (
        <p className="text-red-500 text-sm mt-2">{error}</p>
      )}
    </div>
  );
};

export default SimpleCaptcha;
